import React, { memo, useCallback, useMemo } from "react";
import { HASH_KEY, MENTION_TYPE } from "modules/utils/Constants";
import CopyListPopover from "./copy-list-popover";
import AddItemButton from "../components/add-item-button";
import { Box } from "@chakra-ui/react";
import { useTags } from "modules/providers/tags-provider";
import TextHighlighter from "modules/_shared/text-highlighter";
import { IRelatedMessageThread } from "modules/providers/index-db-provider/index-db-models";
import { MessageThreadType } from "gql/graphql";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { IQuillEditorHandle } from "modules/messaging/compose/quill-message-composer";

interface IProps {
  draftId: string;
  relatedMessageThread: IRelatedMessageThread;
  quillEditorRef: React.RefObject<IQuillEditorHandle>;
}

const CopyTagsPopover = ({
  draftId,
  relatedMessageThread,
  quillEditorRef,
}: IProps) => {
  const { draft } = useMessageThreadDraft({ draftId });
  const { tagsById } = useTags();

  const onSaveClicked = useCallback(
    (selectedIds: string[]) => {
      selectedIds.forEach((id) => {
        const tag = tagsById[id];
        if (!tag) return;
        quillEditorRef.current?.appendMention({
          id,
          isExternal: false,
          type: MENTION_TYPE.TAG,
          value: tag.displayName,
        });
      });
    },
    [tagsById, quillEditorRef],
  );

  const _tagIds = useMemo(
    () =>
      (relatedMessageThread.tagIdList ?? []).filter(
        (id) =>
          tagsById[id]?.active === true && !draft?.tagIdList?.includes(id),
      ),
    [draft?.tagIdList, relatedMessageThread.tagIdList, tagsById],
  );

  const canCopyTags = useMemo(
    () =>
      draft?.type !== MessageThreadType.Chat &&
      relatedMessageThread?.type !== MessageThreadType.Chat,
    [draft?.type, relatedMessageThread?.type],
  );

  const isDisabled = _tagIds.length === 0;

  const title = useMemo(() => {
    if (_tagIds.length > 0) {
      return `Tags (${_tagIds.length})`;
    }
    return "Tags";
  }, [_tagIds.length]);

  if (!canCopyTags) {
    return null;
  }

  return (
    <CopyListPopover
      sections={[
        {
          ids: _tagIds,
          title: "Tags",
        },
      ]}
      title="Add Tags"
      saveTitle="Add Tags"
      renderRow={(_sectionIndex, _rowIndex, id) => (
        <Box fontSize={14}>
          <TextHighlighter
            suggestionDataItem={{
              id,
              display: `${HASH_KEY}${tagsById[id]?.displayName}`,
            }}
            search=""
          />
        </Box>
      )}
      onSaveClicked={onSaveClicked}
    >
      <AddItemButton isDisabled={isDisabled}>{title}</AddItemButton>
    </CopyListPopover>
  );
};

export default memo(CopyTagsPopover);
